import { useMemo } from "react";
import { useSketchReveal } from "@/components/cursors/use-sketch-reveal";
import { useT } from "@/i18n";
import { desktopShellMessages } from "@/i18n/messages/desktop-shell";
import { agentInkStroke } from "@/lib/sketch-stroke/agent-ink-stroke";
import {
  AGENT_NIB,
  INK_ALPHA,
  SKETCH_INK_OVERFLOW_PX,
} from "@/lib/sketch-stroke/ink-style";
import {
  sketchShapePoints,
  sketchStrokePaths,
} from "@/lib/sketch-stroke/sketch-mark";
import { useAgentInk } from "@/stores/ink-settings-store";

/** Single-line fields are underlined; anything taller is boxed. */
const UNDERLINE_MAX_HEIGHT_PT = 44;
const CUE_REVEAL_MS = 240;
const LABEL_FONT_PX = 16;

export type FieldEditCue = {
  id: number;
  name: string;
  status: "verified" | "uncertain";
  bounds: { x: number; y: number; width: number; height: number };
};

/** One field cue as a quick pen stroke: an underline (or a box for tall text
 * areas) with a handwritten label above-left. Fading is CSS-only
 * (`denker-sketch-cue`); the parent removes the mark. */
export function FieldEditCueMark({
  cue,
  left,
  top,
}: {
  cue: FieldEditCue;
  left: number;
  top: number;
}) {
  const t = useT(desktopShellMessages);
  const agentInk = useAgentInk();
  const ink = cue.status === "verified" ? agentInk : "var(--color-warning)";
  const { width, height } = cue.bounds;
  const shape = height <= UNDERLINE_MAX_HEIGHT_PT ? "underline" : "rectangle";
  const seed = `field-cue:${cue.id}`;
  const points = useMemo(
    () => agentInkStroke(sketchShapePoints(shape, width, height, seed), seed),
    [seed, shape, width, height],
  );
  const progress = useSketchReveal({
    key: seed,
    durationMs: CUE_REVEAL_MS,
    enabled: true,
  });
  const outline = sketchStrokePaths(
    [{ points, nib: AGENT_NIB }],
    progress,
  ).join(" ");

  return (
    <div
      className="denker-sketch-cue absolute"
      data-testid="desktop-field-edit-cue"
      data-status={cue.status}
      data-cue-shape={shape}
      style={{ left, top, width, height, color: ink }}
    >
      <svg
        className="absolute"
        style={{
          left: -SKETCH_INK_OVERFLOW_PX,
          top: -SKETCH_INK_OVERFLOW_PX,
          width: width + SKETCH_INK_OVERFLOW_PX * 2,
          height: height + SKETCH_INK_OVERFLOW_PX * 2,
        }}
        viewBox={`${-SKETCH_INK_OVERFLOW_PX} ${-SKETCH_INK_OVERFLOW_PX} ${
          width + SKETCH_INK_OVERFLOW_PX * 2
        } ${height + SKETCH_INK_OVERFLOW_PX * 2}`}
      >
        {outline ? (
          <path
            d={outline}
            fill="currentColor"
            fillOpacity={INK_ALPHA}
            data-testid="desktop-field-edit-cue-stroke"
          />
        ) : null}
      </svg>
      <span
        className="absolute bottom-full left-0 mb-1 whitespace-nowrap font-handwriting leading-none"
        style={{ fontSize: LABEL_FONT_PX }}
      >
        {cue.status === "verified"
          ? cue.name
          : t("fieldCheck", { name: cue.name })}
      </span>
    </div>
  );
}
